import React, { useState } from 'react';
import '../styles/DomainDetail.css';

function getStatusInfo(status) {
  switch (status) {
    case 'active':
      return { dot: '🟢', label: 'Active' };
    case 'pending':
    case 'registering':
      return { dot: '🟡', label: 'Pending' };
    case 'expired':
    case 'failed':
      return { dot: '🔴', label: status.charAt(0).toUpperCase() + status.slice(1) };
    default:
      return { dot: '⚪', label: status || 'Unknown' };
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCents(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function CollapsibleSection({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="domain-section">
      <button className="domain-section-header" onClick={() => setOpen(!open)}>
        <span className="domain-section-chevron">{open ? '▼' : '▶'}</span>
        <span className="domain-section-title">{title}</span>
      </button>
      {open && <div className="domain-section-body">{children}</div>}
    </div>
  );
}

export default function DomainDetail({ domain }) {
  if (!domain) {
    return (
      <div className="domain-detail-empty">
        <p>Select a domain from the list to view its details.</p>
      </div>
    );
  }

  const status = getStatusInfo(domain.status);
  const registrarNote = domain.registered_by === 'byod'
    ? 'Customer-owned domain (BYOD)'
    : 'Registered via The Idea Machine through Cloudflare';

  return (
    <div className="domain-detail">
      {/* Header — always visible */}
      <div className="domain-detail-header">
        <div className="domain-detail-title-row">
          <h2 className="domain-detail-name">{domain.domain_name}</h2>
          <span className="domain-detail-status">
            {status.dot} {status.label}
          </span>
        </div>
        <p className="domain-detail-registrar">{registrarNote}</p>
      </div>

      {/* Linked Website */}
      <div className="domain-detail-card">
        <h3 className="domain-card-title">Linked Website</h3>
        {domain.site ? (
          <div className="domain-linked-site">
            <span className="domain-site-icon">🔗</span>
            <a
              href={`https://${domain.domain_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="domain-site-link"
            >
              {domain.domain_name}
              <span className="domain-site-arrow"> →</span>
            </a>
            <div className="domain-preview-url">
              <span className="domain-site-icon">🖥️</span>
              <a
                href={domain.site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="domain-site-link domain-site-link-secondary"
              >
                Preview: {domain.site.url?.replace(/^https?:\/\//, '')}
              </a>
            </div>
          </div>
        ) : (
          <p className="domain-no-site">No website linked yet</p>
        )}
      </div>

      {/* Registration Details — expanded by default */}
      <CollapsibleSection title="Registration Details" defaultOpen={true}>
        <div className="domain-detail-grid">
          <div className="domain-detail-row">
            <span className="domain-detail-label">Registered</span>
            <span className="domain-detail-value">{formatDate(domain.registered_at)}</span>
          </div>
          <div className="domain-detail-row">
            <span className="domain-detail-label">Expires</span>
            <span className="domain-detail-value">{formatDate(domain.expires_at)}</span>
          </div>
          <div className="domain-detail-row">
            <span className="domain-detail-label">Auto-renew</span>
            <span className="domain-detail-value">{domain.auto_renew ? 'On' : 'Off'}</span>
          </div>
          <div className="domain-detail-row">
            <span className="domain-detail-label">You paid</span>
            <span className="domain-detail-value">{formatCents(domain.quoted_customer_cents)}</span>
          </div>
          <div className="domain-detail-row">
            <span className="domain-detail-label">Annual renewal</span>
            <span className="domain-detail-value">{formatCents(domain.renewal_cost_cents)}</span>
          </div>
        </div>
      </CollapsibleSection>

      {/* Technical Details — collapsed by default */}
      <CollapsibleSection title="Technical Details" defaultOpen={false}>
        <div className="domain-detail-grid">
          <div className="domain-detail-row">
            <span className="domain-detail-label">Registration ID</span>
            <span className="domain-detail-value domain-detail-mono">
              {domain.cf_registration_id || '—'}
            </span>
          </div>
          <div className="domain-detail-row">
            <span className="domain-detail-label">Type</span>
            <span className="domain-detail-value">
              {domain.registered_by === 'byod' ? 'BYOD (Bring Your Own)' : 'TIM-registered'}
            </span>
          </div>
          {domain.registered_by === 'byod' && (
            <>
              <div className="domain-detail-row">
                <span className="domain-detail-label">CNAME target</span>
                <span className="domain-detail-value domain-detail-mono">
                  {domain.byod_cname_target || '—'}
                </span>
              </div>
              <div className="domain-detail-row">
                <span className="domain-detail-label">DNS verified</span>
                <span className="domain-detail-value">
                  {domain.byod_verified ? 'Yes' : 'No'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* ICANN registrant rights link */}
        <div className="domain-icann-link">
          <a
            href="https://www.icann.org/resources/pages/registrant-rights-responsibilities"
            target="_blank"
            rel="noopener noreferrer"
          >
            Your rights as a domain registrant →
          </a>
        </div>
      </CollapsibleSection>
    </div>
  );
}
